import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import cx from 'classnames';
import _ from 'lodash';
import Truncate from "react-truncate";
import { useParams } from "react-router-dom";
import Tooltip from "components/Tooltip";

import { TrendsPieChart } from './charts'
import {DropdownFilter} from './dropDown'

import { EmptyMessage, LoadingMessage, MetricsHeader, TableInfo } from '.';
import { convertDatesToTimezone } from 'services/report.service';
import { fetchPageMetricsForType, fetchChartMetricsForType } from 'actions/storyboardReports';
import { TableFooter } from './tableFooter';
import { useReportType } from '../ReportsProvider';
import { formatDurationWithLabel, safeDivide } from '../reporthelpers';

import styles from '../report.module.scss';

export function MetricsAnalytics(props) {
  const dispatch = useDispatch();

  let { storyboardId } = useParams();
  const metrics = useSelector(state => _.get(state, `storyboards.storyboard.reports.metrics.${props.metricType}.items`));
  const chartData = useSelector(state => _.get(state, `storyboards.storyboard.reports.metrics.${props.metricType}.chartItems`));
  const pagination = useSelector(state => _.get(state, `storyboards.storyboard.reports.metrics.${props.metricType}.pagination`));
  const loading = useSelector(state => _.get(state, `storyboards.storyboard.reports.metrics.${props.metricType}.loading`));
  const [currentChart, setCurrentChart] = useState("table_view");
  const [viewOption, setViewOption] = useState({ label: 'Views', value: 'views', name: props.tableName === 'Account Analytics'? 'organization.name' : 'metricType' })
  const { reportType } = useReportType()

  const handleChartChange = (value) => {
    setCurrentChart(value)
  }

  useEffect(() => {
    if (props.date) {
      handlePageChange(0, props.metricType);
    }
  }, [props.date, reportType]);

  const handlePageChange = (page, type) => {
    var request = {
      storyboard: storyboardId,
      metricType: type,
      reportType,
      ...convertDatesToTimezone(props.date),
    }

    dispatch(fetchChartMetricsForType(request, 0, 6));
    dispatch(fetchPageMetricsForType(request, page, 5));
  }

  const handleArrange = (data) => {
    setViewOption(state =>{
      return {
        ...state,
        label: data.label,
        value: data.value
      }
    });
  }

  return (
    <div className={styles.metricsAnalyticsWrapper}>
      <div className={styles.metricsAnalyticsContainer}>
        <TableInfo
          name={props.tableName}
          pagination={pagination} />

        {currentChart == "table_view" && <MetricsHeader />}

        {currentChart == "pie_chart_view" && <DropdownFilter disableOption={[]}  arrangeOption={viewOption} handleArrange={handleArrange} />}

        {currentChart == "table_view" &&
          !loading && metrics && metrics.map((data, index) => {
            return <MetricsReportView loading={loading} type={props.metricType} data={data} index={index} />
          })
        }

        {(_.size(chartData) > 0  && currentChart == "pie_chart_view") &&
          <div className={styles.graphSmallView}>
            <TrendsPieChart data={chartData} viewOption={viewOption} height={320} width="100%" />
          </div>
        }

        {loading && (metrics && metrics.length > 0) &&
          <LoadingMessage />
        }

        {( metrics && metrics.length == 0) && !loading &&
          <EmptyMessage />
        }
      </div>

      <TableFooter
        currentChart={currentChart}
        disableView={['graph_view']}
        handleChartChange={handleChartChange}
        handlePageChange={(page) => handlePageChange(page, props.metricType)}
        pagination={pagination}
        leftAlign={true}
        paginationName={props.paginationName}
      />
    </div>
  )
}

function MetricsReportView({ loading, type, data }) {

  const [tooltip, setTooltip] = useState(false);
  
  if (!data || data.length <= 1) {
    return false;
  }

  const renderOrganizationName = (data) => {
    if (data.organization) {
      return data.organization.name;
    }

    if (data.contact && data.contact.company) {
      return capitalizeFirstLetter(data.contact.company);
    }

    return renderCompanyNameFromEmail(data.contact.email);
  }

  const capitalizeFirstLetter = (value) => {
    if (value) {
      value = value.charAt(0).toUpperCase() + value.slice(1);
    }

    return value;
  }

  const renderCompanyNameFromEmail = (email) => {
    const domain = email.split("@")[1];
    const company = domain.split(".")[0];
    return capitalizeFirstLetter(company)
  }

  const handleTooltip = (truncated) => {
    setTooltip(truncated);
  }

  return (
    <div className={styles.metricsAnalyticsRow}>
      <div className={cx(styles.wrapper, styles.nameColumn)}>
        <div className={styles.headline}>
        <Tooltip placement="top" disabled={!tooltip} content={type == "organization" ? renderOrganizationName(data) : data.metricType}>
          <Truncate onTruncate={handleTooltip} width={90} lines={1}>
            {type == "organization" ? renderOrganizationName(data) : data.metricType}
          </Truncate>
        </Tooltip>
        </div>
      </div>

      <div className={cx(styles.wrapper, styles.viewsColumn)}>
        <div className={styles.headline}>{data.views}</div>
      </div>

      <div className={cx(styles.wrapper, styles.visitorsColumn)}>
        <div className={styles.headline}>{data.visitors}</div>
      </div>

       <div className={cx(styles.wrapper, styles.leadsColumn)}>
        <div className={styles.headline}>{data.leads}</div>
      </div>

      <div className={cx(styles.wrapper, styles.timeSpentColumn)}>
        <div className={styles.headline}>{formatDurationWithLabel(data.timeSpent).value}</div>
      </div>

      <div className={cx(styles.wrapper, styles.bounceRateColumn)}>
        <div className={styles.headline}>{formatDurationWithLabel(safeDivide(data.timeSpent, data.views)).value}</div>
      </div>
      <div className={cx(styles.wrapper, styles.bounceRateColumn)}>
        <div className={styles.headline}>{`${Math.round(data.bounceRate)}%`}</div>
      </div>
    </div>
  )
}